<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import dayjs from 'dayjs';
  import customParseFormat from 'dayjs/plugin/customParseFormat';
  import { InlineNotification, Modal, TextInput } from 'carbon-components-svelte';

  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import { getPathDetails, updatePathDetails } from '../../services';

  export let open = false;
  export let path;

  let loading = true;
  let pathDetails;
  let notification;
  let completed = false;

  let dates = {
    data: {},
    errors: {},
  };

  dayjs.extend(customParseFormat);

  const dispatch = createEventDispatcher();

  async function submitModal() {
    if (isInvalidForm(dates) || !pathDetails.id) {
      return;
    }

    loading = true;

    try {
      await updatePathDetails(pathDetails.id, dates.data);
      completed = true;
      notification = {
        kind: 'success',
        title: 'Success:',
        subtitle: 'Path range updated successfully.',
      };
    } catch (error) {
      console.error('[RawDataFeedPathDatesModal] Failed to update path details', error);
      notification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to update path range. Close modal and try again.',
      };
    } finally {
      loading = false;
    }
  }

  function isInvalidForm(updatedDates) {
    return !!updatedDates.errors?.startDate || !!updatedDates.errors?.endDate;
  }

  function handleChangeDate({ target: { name, value } }) {
    const validDate = !value || dayjs(value, 'YYYY-MM-DD', true).isValid();

    dates = {
      data: { ...dates.data, [name]: value },
      errors: { ...dates.errors, [name]: validDate ? null : 'Format must be YYYY-MM-DD' },
    };

    const { startDate, endDate } = dates.data;
    if (startDate && endDate && validDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);

      dates = {
        data: { ...dates.data },
        errors: {
          ...dates.errors,
          [name]:
            name === 'startDate' && end.isBefore(start)
              ? 'Start date must be before end date'
              : name === 'endDate' && start.isAfter(end)
              ? 'End date must be after start date'
              : null,
        },
      };
    }
  }

  function onModalClose() {
    dispatch('close');
  }

  onMount(async () => {
    try {
      pathDetails = await getPathDetails(path);
      if (!pathDetails) {
        notification = {
          kind: 'error',
          title: 'Error:',
          subtitle: 'Selected path is not saved. Save the new path first in order to be able to define range.',
        };
      }

      dates = {
        data: {
          ...(pathDetails?.startDate ? { startDate: pathDetails.startDate } : {}),
          ...(pathDetails?.endDate ? { endDate: pathDetails.endDate } : {}),
        },
        errors: {},
      };
    } catch (error) {
      console.error('[RawDataFeedPathDatesModal] Failed to get path details', error);
      notification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to get path details.',
      };
    } finally {
      loading = false;
    }
  });

  $: submitDisabled = isInvalidForm(dates) || completed;
  $: hasError = notification?.kind === 'error';
</script>

<Modal
  bind:open
  on:close={onModalClose}
  modalHeading="Raw Feed Path Range"
  modalLabel={path}
  primaryButtonText="Update"
  primaryButtonDisabled={hasError || submitDisabled || loading}
  preventCloseOnClickOutside={true}
  on:click:button--primary={submitModal}
>
  <LoadingSpinner {loading}>
    {#if notification}
      <InlineNotification hideCloseButton lowContrast {...notification} />
    {/if}
    <TextInput
      disabled={hasError || loading || completed}
      name="startDate"
      labelText="Start Date"
      placeholder="Start date"
      size="sm"
      invalid={!!dates.errors?.startDate}
      invalidText={dates.errors?.startDate}
      value={dates.data?.startDate}
      on:blur={handleChangeDate}
    />
    <TextInput
      disabled={hasError || loading || completed}
      name="endDate"
      labelText="End Date"
      placeholder="End date"
      size="sm"
      invalid={!!dates.errors?.endDate}
      invalidText={dates.errors?.endDate}
      value={dates.data?.endDate}
      on:blur={handleChangeDate}
    />
  </LoadingSpinner>
</Modal>
